/**
 * Board icon set — the fixed, in-repo pictogram vocabulary of the park's
 * information board (spec `board-visual-language`, FR-3 / FR-4;
 * anton/forrest#1402).
 *
 * The physical board announces every rule with a pictogram inside a filled
 * circle. The operator picks one of these by NAME in the rules field; she never
 * uploads artwork (FR-3), and an unknown or missing name falls back to `paw`
 * rather than erroring (FR-4).
 *
 * Drawn here as plain SVG geometry — no icon dependency, no request-time fetch
 * from a third-party host (NFR-3). Each entry is the INNER markup of a
 * 24×24 `viewBox` symbol, stroked in `currentColor` so the badge's ink token
 * drives the colour; `fill="none"` is set on the wrapping <svg>.
 *
 * Adding an icon = one entry here. Keep the visual weight consistent:
 * 24×24 box, ~2px stroke, round caps/joins, geometry inset ~2px from the edge.
 */

/** Icon name → inner SVG markup of a 24×24 symbol. */
export const BOARD_ICONS: Record<string, string> = {
	// Default / catch-all: a dog's paw.
	paw: '<circle cx="7" cy="9" r="1.9"/><circle cx="12" cy="7" r="1.9"/><circle cx="17" cy="9" r="1.9"/><path d="M12 11.5c-2.6 0-4.8 2-4.8 4.3 0 1.6 1.2 2.7 2.8 2.7 1 0 1.4-.4 2-.4s1 .4 2 .4c1.6 0 2.8-1.1 2.8-2.7 0-2.3-2.2-4.3-4.8-4.3Z"/>',
	// "Keep an eye on your dog."
	eye: '<path d="M2.5 12S6 6.5 12 6.5 21.5 12 21.5 12 18 17.5 12 17.5 2.5 12 2.5 12Z"/><circle cx="12" cy="12" r="2.8"/>',
	// Health / vaccination: a cross inside a shield.
	health:
		'<path d="M12 2.8 4.8 5.9v5.4c0 4.4 3 8.3 7.2 9.9 4.2-1.6 7.2-5.5 7.2-9.9V5.9Z"/><path d="M12 8.4v6.4M8.8 11.6h6.4"/>',
	// Own responsibility: a shield with a check.
	shield:
		'<path d="M12 2.8 4.8 5.9v5.4c0 4.4 3 8.3 7.2 9.9 4.2-1.6 7.2-5.5 7.2-9.9V5.9Z"/><path d="m9 11.9 2.2 2.2 4-4.2"/>',
	// Never leave the dog alone: a dog's face (ears, eyes, muzzle).
	dog: '<path d="M7.4 5.2c-1.4 0-2.5 1.2-2.5 2.7v3.5c0 3.6 3.2 6.5 7.1 6.5s7.1-2.9 7.1-6.5V7.9c0-1.5-1.1-2.7-2.5-2.7-1 0-1.9.6-2.3 1.5H9.7c-.4-.9-1.3-1.5-2.3-1.5Z"/><path d="M9.7 11.1h.01M14.3 11.1h.01"/><path d="M12 13.3v1.3M10.5 15.5c.9.6 2.1.6 3 0"/>',
	// No smoking.
	"no-smoking":
		'<circle cx="12" cy="12" r="9.2"/><path d="M5.5 18.5 18.5 5.5"/><path d="M6.6 13.9h8.9v2.6H6.6z"/>',
	// Leave no litter: a waste bin.
	trash:
		'<path d="M4.8 6.7h14.4M9.2 6.7V4.9h5.6v1.8M6.6 6.7l.9 12.2c0 .9.7 1.5 1.6 1.5h5.8c.9 0 1.6-.6 1.6-1.5l.9-12.2"/><path d="M10.3 10.3v6.4M13.7 10.3v6.4"/>',
	// Pick up the droppings: a tied waste bag.
	poop: '<path d="M8.4 8.4h7.2l1.4 8.2a3.1 3.1 0 0 1-3 3.6H10a3.1 3.1 0 0 1-3-3.6Z"/><path d="M9.6 8.4a2.4 2.4 0 0 1 4.8 0"/><path d="M9.4 4.3c1 .9 2.4 1.3 3.8 1.1"/>',
	// Fires and grilling only where marked.
	fire: '<path d="M12 2.9s.9 3-1.4 5.4c-2 2.1-4 3.6-4 6.6a5.4 5.4 0 0 0 10.8 0c0-2-.9-3.3-2-4.4 0 1.4-.9 2.3-1.8 2.3-1.3 0-1.9-1.1-1.4-2.6.5-1.6 1.4-4.1-.2-7.3Z"/>',
	// Report faults: a telephone handset.
	phone:
		'<path d="M8.2 3.6c.6 0 1.1.4 1.3 1l.9 3a1.4 1.4 0 0 1-.5 1.5l-1.4 1a11.6 11.6 0 0 0 5.4 5.4l1-1.4a1.4 1.4 0 0 1 1.5-.5l3 .9c.6.2 1 .7 1 1.3v2.6c0 .9-.8 1.6-1.7 1.5A16.8 16.8 0 0 1 4.1 5.3c-.1-.9.6-1.7 1.5-1.7Z"/>',
	// The park in your pocket: a smartphone (anton/forrest#1982). Distinct from
	// `phone`, which is the handset you CALL the park on — this one is the
	// device the app runs on.
	mobile:
		'<rect x="7" y="2.6" width="10" height="18.8" rx="2.4"/><path d="M10.6 5.2h2.8"/><path d="M10.8 18.6h2.4"/>',
	// Cleanliness / nature: a spruce.
	tree: '<path d="M12 2.9 7.4 9.5h2.4L5.9 15h3.4l-3.4 4.4h12.2L14.7 15h3.4l-3.9-5.5h2.4Z"/><path d="M12 19.4v2"/>',
	// Generic dog-park marker: a bone.
	bone: '<circle cx="6.6" cy="10.1" r="2.3"/><circle cx="6.6" cy="13.9" r="2.3"/><circle cx="17.4" cy="10.1" r="2.3"/><circle cx="17.4" cy="13.9" r="2.3"/><path d="M8.5 10.3h7M8.5 13.7h7"/>',
	// Close the gate carefully: a fenced gate with an arched top.
	gate: '<path d="M3.2 20.2h17.6"/><path d="M5.2 20.2V10a6.8 6.8 0 0 1 13.6 0v10.2"/><path d="M12 8.6v11.6M8.6 12.4v7.8M15.4 12.4v7.8M5.2 15.4h13.6"/>',
	// Arrive and leave within the booked time.
	clock: '<circle cx="12" cy="12" r="9.2"/><path d="M12 6.9V12l3.4 2"/>',
};

/** The icon used when a name is missing or unrecognised (FR-4). */
export const DEFAULT_BOARD_ICON = "paw";

/** Every icon name, for docs/tests and the admin field description. */
export const BOARD_ICON_NAMES: string[] = Object.keys(BOARD_ICONS);

/**
 * Resolve a (possibly operator-typed) icon name to real SVG geometry.
 * Trims and lowercases; anything unknown resolves to the default paw — an
 * unrecognised name is never an error (FR-4, spec edge cases).
 */
export function resolveBoardIcon(name?: string): string {
	const key = (name ?? "").trim().toLowerCase();
	return BOARD_ICONS[key] ?? BOARD_ICONS[DEFAULT_BOARD_ICON];
}

/**
 * A complete `<svg>` for an icon name, ready to drop inside a badge.
 * `aria-hidden` because the icon always accompanies a text title — it carries
 * no information of its own for a screen reader.
 */
export function boardIconSvg(name?: string): string {
	return `<svg class="board-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${resolveBoardIcon(name)}</svg>`;
}
